import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SEO } from '../../components/SEO/SEO';
import { generateBreadcrumbSchema } from '../../components/SEO/schemas';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import {
  Download,
  TrendingUp,
  Schedule,
  CheckCircle,
  Warning,
  Hotel,
  LocationOn,
  KingBed,
  Send,
  Block,
  WarningAmber,
  CheckBox,
  Autorenew,
  InsertDriveFile,
  Assignment,
} from '@mui/icons-material';
import { AppBreadcrumbs } from '../../components/navigation/AppBreadcrumbs';
import { PropertySelectionModal } from '../../components/trendreport/PropertySelectionModal';
import { SpecialInstructionsModal } from '../../components/trendreport/SpecialInstructionsModal';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { brandColors } from '../../theme';


export const TrendReportDetailPage: React.FC = () => {
  const { trendReportId } = useParams();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hotelList, setHotelList] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<number | null>(null);
  const [customerNotes, setCustomerNotes] = useState('');
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitSuccess, setResubmitSuccess] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [instructions, setInstructions] = useState<any[]>([]);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const currentIdRef = useRef<string | undefined>(undefined);

  // Preloaded data for PropertySelectionModal
  const [preloadedProperties, setPreloadedProperties] = useState<any>(null);
  const [preloadedSubmarkets, setPreloadedSubmarkets] = useState<any[]>([]);
  const [preloadedContinentMarkets, setPreloadedContinentMarkets] = useState<any[]>([]);
  const [preloadedMarketName, setPreloadedMarketName] = useState<string>('');

  // Breadcrumb schema for SEO - must be before early returns to comply with Rules of Hooks
  const breadcrumbSchema = useMemo(() => {
    return generateBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'Custom Comp Sets', url: '/custom-comp-sets' },
      { name: report?.report_name || 'Report', url: `/trend-report/${trendReportId}` }
    ]);
  }, [report, trendReportId]);

  // Fetch report details on mount and when ID changes
  useEffect(() => {
    // Check if this is the same ID we already fetched
    if (currentIdRef.current === trendReportId && hasFetchedRef.current) {
      return;
    }
    
    // Update current ID and reset fetch flag if ID changed
    if (currentIdRef.current !== trendReportId) {
      currentIdRef.current = trendReportId;
      hasFetchedRef.current = false;
    }
    
    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const fetchReportDetails = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/store/trend-reports/${trendReportId}`,
          { method: 'GET' }
        );

        if (response?.ok) {
          const data = await response.json();
          setReport(data.trendReport);
          // Load existing requirements if any
          if (data.trendReport?.hotelList || data.trendReport?.requirements) {
            setHotelList(data.trendReport.hotelList || data.trendReport.requirements);
            // If status is needs_adjustments, pre-populate the customerNotes with the hotel list
            if (data.trendReport?.status === 'needs_adjustments') {
              setCustomerNotes(data.trendReport.hotelList || data.trendReport.requirements || '');
            }
          }
        } else if (response?.status === 404) {
          setError('Custom Comp Set Report not found or access denied');
        } else {
          setError('Failed to load Custom Comp Set Report details');
        }
      } catch (error) {
        console.error('Error fetching Custom Comp Set Report:', error);
        setError('An error occurred while loading the report');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReportDetails();
  }, [trendReportId]);

  // Fetch special instructions for the report
  const fetchInstructions = useCallback(async () => {
    if (!trendReportId) return;

    setInstructionsLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/instructions`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setInstructions(data.instructions || []);
      }
    } catch (error) {
      console.error('Error fetching instructions:', error);
    } finally {
      setInstructionsLoading(false);
    }
  }, [authenticatedFetch, trendReportId]);

  // Fetch files for completed reports
  const fetchFiles = useCallback(async () => {
    if (report?.status !== 'completed') return;

    setFilesLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/files`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setFiles(data.files || []);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setFilesLoading(false);
    }
  }, [authenticatedFetch, trendReportId, report?.status]);

  // Preload continent markets for PropertySelectionModal
  const preloadContinentMarkets = useCallback(async () => {
    if (!trendReportId) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/continent-markets`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setPreloadedContinentMarkets(data.markets || []);
      }
    } catch (error) {
      console.error('Error preloading continent markets:', error);
    }
  }, [authenticatedFetch, trendReportId]);

  // Preload properties for current market
  const preloadProperties = useCallback(async () => {
    if (!trendReportId) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/market-properties`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setPreloadedProperties({
          properties: data.properties || [],
          propertiesByClass: data.propertiesByClass || {}
        });
        setPreloadedMarketName(data.marketName || '');
      }
    } catch (error) {
      console.error('Error preloading properties:', error);
    }
  }, [authenticatedFetch, trendReportId]);

  // Preload submarkets for current market
  const preloadSubmarkets = useCallback(async () => {
    if (!trendReportId) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/submarkets`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setPreloadedSubmarkets(data.submarkets || []);
      }
    } catch (error) {
      console.error('Error preloading submarkets:', error);
    }
  }, [authenticatedFetch, trendReportId]);

  // Handle file download
  const handleFileDownload = async (fileId: number, _fileName: string) => {
    setDownloadingFile(fileId);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/files/${fileId}/download-url`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        // Open the download URL in a new tab
        window.open(data.url, '_blank');
      } else {
        alert('Failed to generate download link. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('An error occurred while downloading the file.');
    } finally {
      setDownloadingFile(null);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fetch files when report status is completed
  useEffect(() => {
    if (report?.status === 'completed') {
      fetchFiles();
    }
  }, [report?.status, trendReportId]);

  // Fetch instructions when report is loaded
  useEffect(() => {
    if (report) {
      fetchInstructions();
    }
  }, [report, fetchInstructions]);

  // Preload modal data immediately when page loads
  useEffect(() => {
    if (trendReportId) {
      // Call all preload functions in parallel for better performance
      preloadContinentMarkets();
      preloadProperties();
      preloadSubmarkets();
    }
  }, [trendReportId, preloadContinentMarkets, preloadProperties, preloadSubmarkets]);

  // Separate function for refreshing after submission
  const refreshReportDetails = useCallback(async () => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setReport(data.trendReport);
        // Load existing requirements if any
        if (data.trendReport?.hotelList || data.trendReport?.requirements) {
          setHotelList(data.trendReport.hotelList || data.trendReport.requirements);
          // If status is needs_adjustments, update customerNotes with the hotel list
          if (data.trendReport?.status === 'needs_adjustments') {
            setCustomerNotes(data.trendReport.hotelList || data.trendReport.requirements || '');
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing Custom Comp Set Report:', error);
    }
  }, [authenticatedFetch, trendReportId]);

  const handleSubmitPropertySelection = async () => {
    if (!hotelList.trim()) {
      alert('Please enter your hotel list before submitting');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/requirements`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            requirements: hotelList,
          }),
        }
      );

      if (response?.ok) {
        setSubmitSuccess(true);
        // Refresh report data
        await refreshReportDetails();
        // Show success message for 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        alert('Failed to submit requirements. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting requirements:', error);
      alert('An error occurred while submitting requirements');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubmit = async () => {
    if (!customerNotes.trim()) {
      alert('Please provide your updated hotel list before resubmitting');
      return;
    }

    setResubmitting(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/resubmit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            hotelList: customerNotes,
          }),
        }
      );

      if (response?.ok) {
        setResubmitSuccess(true);
        // Refresh report data
        await refreshReportDetails();
        // Don't clear the customerNotes since it now contains the hotel list
        // Show success message for 5 seconds
        setTimeout(() => setResubmitSuccess(false), 5000);
      } else {
        const data = await response?.json();
        alert(data?.error || 'Failed to resubmit. Please try again.');
      }
    } catch (error) {
      console.error('Error resubmitting Custom Comp Set Report:', error);
      alert('An error occurred while resubmitting');
    } finally {
      setResubmitting(false);
    }
  };


  const getStatusColor = (status: string): 'success' | 'info' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'submitted':
        return 'info';
      case 'needs_adjustments':
        return 'warning';
      case 'purchased':
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          onClick={() => navigate('/subscriptions#trend-reports')}
        >
          Back to Custom Comp Set Reports
        </Button>
      </Container>
    );
  }

  return (
    <>
      <SEO
        title={report ? `${report.report_name} - Custom Comp Set Report` : 'Trend Report'}
        description={report
          ? `Custom competitive set analysis report for ${report.report_name}. View property selection, market trends, and comparative analysis.`
          : 'View your custom trend report details and analysis.'
        }
        robots="noindex, follow"
        schemas={[breadcrumbSchema]}
      />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Market Header with Status Chip */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography
              component="h1"
              sx={{
                color: brandColors.blue,
                fontWeight: 400,
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
                lineHeight: 1.05,
                letterSpacing: '-0.5px',
              }}
            >
            Custom Comp Set Report - {report?.marketName || 'Loading...'}
          </Typography>
          {report && (
            <Chip 
              label={report?.status?.replace('_', ' ').toUpperCase()} 
              color={getStatusColor(report?.status)}
              size="medium"
            />
          )}
        </Box>
      </Box>

      {/* Breadcrumbs */}
      <AppBreadcrumbs
        items={[
          { label: 'My Custom Comp Set Reports', path: '/subscriptions#trend-reports' },
          { label: report?.marketName || 'Custom Comp Set Report' },
        ]}
        sx={{ mb: 4 }}
      />

      {/* Show hotel submission form when status is 'purchased' */}
      {report?.status === 'purchased' ? (
        <>
          <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
            {/* Custom Comp Set Report Image in upper left */}
            <Box sx={{ 
              float: 'left',
              mr: 3,
              mb: 2,
              width: 200,
              height: 150,
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}>
              <img
                src="/images/trend_report_950x720.jpg"
                alt="Custom Comp Set Report"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.display = 'flex';
                    parent.style.alignItems = 'center';
                    parent.style.justifyContent = 'center';
                    parent.innerHTML = '<svg style="font-size: 40px; color: #9e9e9e;" class="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M 6 2 C 4.9 2 4.01 2.9 4.01 4 L 4 20 c 0 1.1 0.89 2 1.99 2 H 18 c 1.1 0 2 -0.9 2 -2 V 8 l -6 -6 H 6 z m 7 7 V 3.5 L 18.5 9 H 13 z"/></svg>';
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <TrendingUp sx={{ fontSize: 40, color: brandColors.blue }} />
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: brandColors.blue,
                    fontWeight: 'bold',
                  }}
                >
                  Submit the Properties or Submarkets for the Report
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  MarketCompass Custom Comp Set Report - {report?.marketName}
                </Typography>
              </Box>
            </Box>

            <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', clear: 'both' }}>
              <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                Next Steps
              </Typography>
              <Typography variant="body1" paragraph>
                A Custom Comp Set Report provides comprehensive market analysis and performance benchmarking for your selected competitive set.
                This report includes detailed metrics such as occupancy rates, ADR (Average Daily Rate), RevPAR (Revenue per Available Room),
                and market penetration indices that help you understand your property's position relative to your competition.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>To create your report, we need you to define your competitive set</strong> by selecting either:
              </Typography>
              <List dense sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Individual Properties"
                    secondary="Select specific hotels that you consider direct competitors"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Submarkets"
                    secondary="Select entire geographic submarkets to include all properties within those areas"
                  />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary">
                For more detailed information about what's included in a Custom Comp Set Report, visit the "Custom Comp Set Report" tab on any
                market detail page.
              </Typography>
            </Paper>

            {submitSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Your property and submarket selection has been submitted successfully! We'll finalize your comp set and start building your report.
              </Alert>
            )}


            {report?.status !== 'purchased' && (
              <Grid container spacing={3}>
                {/* Instructions Section */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', bgcolor: '#e3f2fd' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Hotel /> Step 1: Enter Your Properties
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        For each property, include:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Hotel fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Property Name" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <LocationOn fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Full Address (City, State, Country)" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <KingBed fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Number of Rooms" />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', bgcolor: '#bbdefb' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Block /> Step 2: Follow the Rules
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Block fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Maximum Rules"
                            secondary="Property: 50% / Parent company: 70% / Brand: 50% of total rooms"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <WarningAmber fontSize="small" color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Historical Changes"
                            secondary="Reports may be truncated if room inventory changes"
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%', bgcolor: '#90caf9' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Autorenew /> Step 3: Provide Backups
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckBox fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Primary List"
                            secondary="Your top preferred properties"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Autorenew fontSize="small" color="info" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Secondary List"
                            secondary="Backup properties in order of preference"
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            <Box sx={{ mt: 4 }}>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                <Button
                  variant="contained"
                  onClick={() => setShowPropertyModal(true)}
                  startIcon={<Hotel />}
                  size="large"
                  sx={{
                    backgroundColor: 'rgb(253, 185, 19)',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: 'rgb(228, 166, 17)',
                    }
                  }}
                >
                  Select Properties or Submarkets
                </Button>
              </Box>

              <TextField
                multiline
                fullWidth
                rows={12}
                value={hotelList}
                placeholder={hotelList ? '' : 'Use the "Select Properties or Submarkets" button above to choose properties or submarkets'}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  sx: {
                    maxHeight: '300px',
                    overflow: 'auto',
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    backgroundColor: '#f5f5f5',
                    '&.Mui-focused': {
                      backgroundColor: '#f5f5f5',
                    }
                  }
                }}
                label="Selected Properties"
                helperText="Click the 'Select Properties or Submarkets' button to choose properties or submarkets"
              />

              {/* Special Instructions Section */}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => setShowInstructionsModal(true)}
                  startIcon={<Assignment />}
                  size="large"
                  sx={{
                    mb: 2,
                    backgroundColor: 'rgb(253, 185, 19)',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: 'rgb(228, 166, 17)',
                    }
                  }}
                >
                  Add Special Instructions
                </Button>

                {instructionsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : instructions.length > 0 ? (
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Instruction Type</TableCell>
                          <TableCell>Instructions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {instructions.map((instruction) => (
                          <TableRow key={instruction.id}>
                            <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                              {instruction.instructionTypeName}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {instruction.instruction}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No special instructions added yet.
                  </Typography>
                )}
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Once we have your list, our team will finalize your comp set and get started on your Custom Comp Set Report right away.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Send />}
                  onClick={handleSubmitPropertySelection}
                  disabled={submitting || !hotelList.trim()}
                >
                  {submitting ? 'Submitting...' : 'Submit Property Selection'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </>
      ) : report?.status === 'submitted' ? (
        /* Show enhanced view for submitted status */
        <>
          <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
            {/* Custom Comp Set Report Image in upper left */}
            <Box sx={{ 
              float: 'left',
              mr: 3,
              mb: 2,
              width: 200,
              height: 150,
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}>
              <img
                src="/images/trend_report_950x720.jpg"
                alt="Custom Comp Set Report"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.style.display = 'flex';
                    parent.style.alignItems = 'center';
                    parent.style.justifyContent = 'center';
                    parent.innerHTML = '<svg style="font-size: 40px; color: #9e9e9e;" class="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M 6 2 C 4.9 2 4.01 2.9 4.01 4 L 4 20 c 0 1.1 0.89 2 1.99 2 H 18 c 1.1 0 2 -0.9 2 -2 V 8 l -6 -6 H 6 z m 7 7 V 3.5 L 18.5 9 H 13 z"/></svg>';
                  }
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Autorenew sx={{ fontSize: 40, color: brandColors.blue, animation: 'spin 3s linear infinite' }} />
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{
                    color: brandColors.blue,
                    fontWeight: 'bold',
                  }}
                >
                  Your Report Is Being Created
                </Typography>
              </Box>
            </Box>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                ✅ Property Selection Successfully Received
              </Typography>
              <Typography variant="body2">
                Thank you! We received your property selection on {new Date(report.submittedDate).toLocaleDateString()}
                at {new Date(report.submittedDate).toLocaleTimeString()}.
              </Typography>
            </Alert>

            <Typography variant="h5" sx={{ mb: 2, color: brandColors.blue }}>
              What Happens Next?
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: '#e3f2fd' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CheckBox sx={{ color: brandColors.blue }} />
                      <Typography variant="h6">Step 1: Validation</Typography>
                    </Box>
                    <Typography variant="body2">
                      Our team is reviewing your property selection to ensure it meets the comp set requirements,
                      including the 50% maximum for single properties and brands, and 70% maximum for parent companies.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: '#bbdefb' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <TrendingUp sx={{ color: brandColors.blue }} />
                      <Typography variant="h6">Step 2: Analysis</Typography>
                    </Box>
                    <Typography variant="body2">
                      We're compiling comprehensive market data and performance metrics for your selected hotels, 
                      including historical trends and competitive insights.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: '#90caf9' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Download sx={{ color: 'white' }} />
                      <Typography variant="h6">Step 3: Delivery</Typography>
                    </Box>
                    <Typography variant="body2">
                      Your custom report will be delivered to this page within 1 business day. 
                      You'll receive an email notification when it's ready to download.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper sx={{ p: 3, bgcolor: '#f5f5f5' }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule /> Expected Delivery Timeline
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Your report will be ready by:</strong>{' '}
                {(() => {
                  const submitted = new Date(report.submittedDate);
                  const deadline = new Date(submitted);
                  deadline.setDate(deadline.getDate() + 1);
                  // Skip weekends
                  if (deadline.getDay() === 0) deadline.setDate(deadline.getDate() + 1); // Sunday -> Monday
                  if (deadline.getDay() === 6) deadline.setDate(deadline.getDate() + 2); // Saturday -> Monday
                  return deadline.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                })()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Our team works Monday through Friday. Reports submitted after 5 PM EST or on weekends 
                will be processed the next business day.
              </Typography>
            </Paper>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ mb: 3, color: brandColors.blue }}>
              Your Submitted Property Selection
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                {report.hotelList || report.requirements}
              </Typography>
            </Paper>

            {/* Special Instructions Section */}
            {instructions.length > 0 && (
              <>
                <Typography variant="h5" sx={{ mt: 3, mb: 2, color: brandColors.blue }}>
                  Special Instructions
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Instruction Type</TableCell>
                        <TableCell>Instructions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {instructions.map((instruction) => (
                        <TableRow key={instruction.id}>
                          <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                            {instruction.instructionTypeName}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {instruction.instruction}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <strong>Rest assured:</strong> The Horwath HTL MarketCompass Team is actively working on your custom report.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, ml: 4 }}>
                Our analysts are experts in hotel market analysis and will ensure your report provides 
                valuable insights for your competitive set.
              </Typography>
            </Box>
          </Paper>
        </>
      ) : (
        /* Show existing view for other statuses */
        <Paper elevation={2} sx={{ p: 4 }}>
          {/* Custom Comp Set Report Image in upper left */}
          <Box sx={{ 
            float: 'left',
            mr: 3,
            mb: 2,
            width: 200,
            height: 150,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
          }}>
            <img
              src="/images/trend_report_950x720.jpg"
              alt="Custom Comp Set Report"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.style.display = 'flex';
                  parent.style.alignItems = 'center';
                  parent.style.justifyContent = 'center';
                  parent.innerHTML = '<svg style="font-size: 40px; color: #9e9e9e;" class="MuiSvgIcon-root" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M 6 2 C 4.9 2 4.01 2.9 4.01 4 L 4 20 c 0 1.1 0.89 2 1.99 2 H 18 c 1.1 0 2 -0.9 2 -2 V 8 l -6 -6 H 6 z m 7 7 V 3.5 L 18.5 9 H 13 z"/></svg>';
                }
              }}
            />
          </Box>
          
          {report?.status === 'completed' ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{
                      color: brandColors.blue,
                      fontWeight: 'bold',
                    }}
                  >
                    Your Report Is Ready
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Completed on {report?.completedDate ? new Date(report.completedDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'Recently'}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ✅ Custom Comp Set Report Successfully Completed
                </Typography>
                <Typography variant="body2">
                  Your Custom Comp Set Report for {report?.marketName} has been finalized and is ready for download.
                </Typography>
              </Alert>

              {report?.reportUrl && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Download />}
                    href={report.reportUrl}
                    target="_blank"
                    sx={{ minWidth: 250 }}
                  >
                    Download Your Report
                  </Button>
                </Box>
              )}
            </>
          ) : report?.status === 'needs_adjustments' ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Warning sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{
                      color: brandColors.blue,
                      fontWeight: 'bold',
                    }}
                  >
                    Adjustments Required
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Please review and update your property selection
                  </Typography>
                </Box>
              </Box>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Admin Feedback Received
                </Typography>
                <Typography variant="body2">
                  Our team has reviewed your submission and requires some adjustments to your property selection.
                </Typography>
              </Alert>
            </>
          ) : (
            /* Default status header for other states */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <TrendingUp sx={{ fontSize: 40, color: brandColors.blue }} />
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{
                    color: brandColors.blue,
                    fontWeight: 'bold',
                  }}
                >
                  Custom Comp Set Report Details
                </Typography>
              </Box>
            </Box>
          )}

          <Grid container spacing={3}>
            {(report?.requirements || report?.hotelList) && report?.status !== 'needs_adjustments' && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                      Submitted Property Selection
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {report.hotelList || report.requirements}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Special Instructions Section for completed/other statuses */}
            {instructions.length > 0 && report?.status !== 'needs_adjustments' && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                      Special Instructions
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Instruction Type</TableCell>
                            <TableCell>Instructions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {instructions.map((instruction) => (
                            <TableRow key={instruction.id}>
                              <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                                {instruction.instructionTypeName}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {instruction.instruction}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {report?.notes && report?.status !== 'needs_adjustments' && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {report.notes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Show editable notes section for needs_adjustments status */}
            {report?.status === 'needs_adjustments' && (
              <Grid item xs={12}>
                {resubmitSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Your updated property selection has been submitted successfully! We'll review and complete your report soon.
                  </Alert>
                )}

                <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                  Admin Feedback
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'warning.light', mb: 3 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {report.notes}
                  </Typography>
                </Paper>

                <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                  Update Your Hotel List
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    Please review the admin feedback above and update your hotel list.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setShowPropertyModal(true)}
                    startIcon={<Hotel />}
                  >
                    Select from Property List
                  </Button>
                </Box>

                <TextField
                  multiline
                  fullWidth
                  rows={12}
                  value={customerNotes}
                  placeholder={customerNotes ? '' : 'Use the "Select from Property List" button above to choose properties or submarkets'}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                      '&.Mui-focused': {
                        backgroundColor: '#f5f5f5',
                      }
                    }
                  }}
                  label="Updated Hotel List"
                  helperText="Click the 'Select from Property List' button to update your hotel selection"
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCustomerNotes(report.hotelList || report.requirements || '')}
                    disabled={resubmitting}
                  >
                    Reset to Original
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Send />}
                    onClick={handleResubmit}
                    disabled={resubmitting || !customerNotes.trim()}
                  >
                    {resubmitting ? 'Resubmitting...' : 'Resubmit Updated Hotel List'}
                  </Button>
                </Box>
              </Grid>
            )}

            {/* Display files for completed reports */}
            {report?.status === 'completed' && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: brandColors.blue }}>
                      Report Documents
                    </Typography>
                    
                    {filesLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : files.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Document Name</TableCell>
                              <TableCell align="right">Size</TableCell>
                              <TableCell align="right">Uploaded</TableCell>
                              <TableCell align="center">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {files.map((file) => (
                              <TableRow key={file.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InsertDriveFile sx={{ fontSize: 20, color: brandColors.blue }} />
                                    <Typography variant="body2">{file.fileName}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {formatFileSize(file.fileSize)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {new Date(file.uploadedAt).toLocaleDateString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleFileDownload(file.id, file.fileName)}
                                    disabled={downloadingFile === file.id}
                                    size="small"
                                  >
                                    {downloadingFile === file.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <Download />
                                    )}
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No additional documents available at this time.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {report?.status === 'completed' && !report?.reportUrl && (
            <Alert severity="info" sx={{ mt: 3 }}>
              Your Custom Comp Set Report is complete and you can download it now.
            </Alert>
          )}
        </Paper>
      )}

      {/* Property Selection Modal */}
      {trendReportId && (
        <PropertySelectionModal
          open={showPropertyModal}
          onClose={() => setShowPropertyModal(false)}
          trendReportId={trendReportId}
          onConfirm={(selection: string) => {
            // Update the appropriate field based on report status
            if (report?.status === 'needs_adjustments') {
              setCustomerNotes(selection);
            } else {
              setHotelList(selection);
            }
          }}
          initialSelection={report?.status === 'needs_adjustments' ? customerNotes : hotelList}
          preloadedData={{
            properties: preloadedProperties?.properties,
            propertiesByClass: preloadedProperties?.propertiesByClass,
            submarkets: preloadedSubmarkets,
            continentMarkets: preloadedContinentMarkets,
            marketName: preloadedMarketName
          }}
        />
      )}

      {/* Special Instructions Modal */}
      {trendReportId && (
        <SpecialInstructionsModal
          open={showInstructionsModal}
          onClose={() => setShowInstructionsModal(false)}
          trendReportId={trendReportId}
          existingInstructionTypeIds={instructions.map(i => i.instructionTypeId)}
          onSave={() => {
            fetchInstructions();
            setShowInstructionsModal(false);
          }}
        />
      )}
    </Container>
    </>
  );
};

export default TrendReportDetailPage;